import { useEffect, useRef } from 'react';
import { scrollElementTo } from '@/utils';

const DRAG_CLICK_THRESHOLD = 10;

interface UseHorizontalScrollProps {
  scrollingRef: React.RefObject<HTMLDivElement | null>;
}

export const useHorizontalScroll = ({ scrollingRef }: UseHorizontalScrollProps) => {
  const drag = useRef({ startX: 0, startScrollLeft: 0, distance: 0, frame: null as number | null });

  useEffect(() => {
    const findScrollingNode = (target: EventTarget | null) => {
      const current = scrollingRef.current;

      return current && target instanceof Node && current.contains(target) ? current : null;
    };

    const cancelFrame = () => {
      if (drag.current.frame !== null) {
        cancelAnimationFrame(drag.current.frame);
        drag.current.frame = null;
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      const current = scrollingRef.current;

      if (!current) return;

      const moved = event.clientX - drag.current.startX;

      drag.current.distance = Math.abs(moved);

      if (drag.current.distance < DRAG_CLICK_THRESHOLD || drag.current.frame !== null) return;

      const left = drag.current.startScrollLeft - moved;

      drag.current.frame = requestAnimationFrame(() => {
        drag.current.frame = null;

        scrollElementTo({ element: current, left, behavior: 'auto' });
      });
    };

    const handleMouseUp = () => {
      cancelFrame();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDown = (event: MouseEvent) => {
      const current = findScrollingNode(event.target);

      if (!current) return;

      event.preventDefault();

      drag.current = { startX: event.clientX, startScrollLeft: current.scrollLeft, distance: 0, frame: null };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    };

    const handleClickCapture = (event: MouseEvent) => {
      if (drag.current.distance < DRAG_CLICK_THRESHOLD || !findScrollingNode(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      drag.current.distance = 0;
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('click', handleClickCapture, true);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('click', handleClickCapture, true);
      handleMouseUp();
    };
  }, [scrollingRef]);
};
